import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext.js';
import { chatAPI } from '../api/chatAPI.js';
import { supportChatAPI } from '../api/supportChatAPI.js';
import { createRealtimeConnection } from '../api/realtimeAPI.js';
import { getActiveStoreId } from '../api/storeHeaders.js';
import { getAuthToken } from '../Utils/authToken.js';

const RealtimeContext = createContext(null);

function getToken() {
    return getAuthToken();
}

function emitSupportStoreJoin(socket) {
    if (!socket?.connected) return;
    const storeId = getActiveStoreId();
    if (storeId) {
        socket.emit('support:join-store', { storeId });
    }
}

export const useRealtime = () => {
    const context = useContext(RealtimeContext);
    if (!context) {
        throw new Error('useRealtime must be used within a RealtimeProvider');
    }
    return context;
};

export const RealtimeProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [socket, setSocket] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [teamChatUnread, setTeamChatUnread] = useState(0);
    const [supportInboxUnread, setSupportInboxUnread] = useState(0);
    const refreshTimerRef = useRef(null);
    const socketRef = useRef(null);

    const refreshUnreadCounts = useCallback(async () => {
        if (!getToken()) {
            setTeamChatUnread(0);
            setSupportInboxUnread(0);
            return;
        }

        try {
            const [teamRes, supportRes] = await Promise.all([
                chatAPI.getConversations(),
                supportChatAPI.getConversations(),
            ]);

            const teamTotal = (teamRes.data.conversations || []).reduce(
                (sum, item) => sum + (item.unreadCount || 0),
                0
            );
            const supportTotal = Number.isFinite(supportRes.data.totalUnread)
                ? supportRes.data.totalUnread
                : (supportRes.data.conversations || []).reduce(
                    (sum, item) => sum + (item.unreadCount || 0),
                    0
                );

            setTeamChatUnread(teamTotal);
            setSupportInboxUnread(supportTotal);
        } catch (error) {
            console.error('Failed to refresh chat unread counts:', error);
        }
    }, []);

    const scheduleUnreadRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
            refreshUnreadCounts();
        }, 300);
    }, [refreshUnreadCounts]);

    useEffect(() => {
        if (!isAuthenticated) {
            setSocket(null);
            socketRef.current = null;
            setConnectionStatus('idle');
            setTeamChatUnread(0);
            setSupportInboxUnread(0);
            return undefined;
        }

        refreshUnreadCounts();

        const token = getToken();
        if (!token) return undefined;

        const instance = createRealtimeConnection(token);
        socketRef.current = instance;
        setSocket(instance);
        setConnectionStatus('connecting');

        const onConnect = () => {
            setConnectionStatus('connected');
            emitSupportStoreJoin(instance);
            refreshUnreadCounts();
        };
        const onDisconnect = (reason) => {
            if (reason === 'io client disconnect') return;
            setConnectionStatus('disconnected');
        };
        const onReconnectAttempt = () => setConnectionStatus('reconnecting');
        const onReconnect = () => {
            setConnectionStatus('connected');
            emitSupportStoreJoin(instance);
            refreshUnreadCounts();
        };
        const onConnectError = () => setConnectionStatus('reconnecting');

        instance.on('connect', onConnect);
        instance.on('disconnect', onDisconnect);
        instance.io.on('reconnect_attempt', onReconnectAttempt);
        instance.io.on('reconnect', onReconnect);
        instance.on('connect_error', onConnectError);

        instance.on('chat:conversation-updated', scheduleUnreadRefresh);
        instance.on('chat:message', scheduleUnreadRefresh);
        instance.on('support:conversation-updated', scheduleUnreadRefresh);
        instance.on('support:message', scheduleUnreadRefresh);

        const onStoreChanged = () => {
            emitSupportStoreJoin(socketRef.current);
            scheduleUnreadRefresh();
        };
        window.addEventListener('quazbit-active-store-changed', onStoreChanged);

        return () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            window.removeEventListener('quazbit-active-store-changed', onStoreChanged);
            instance.off('connect', onConnect);
            instance.off('disconnect', onDisconnect);
            instance.io.off('reconnect_attempt', onReconnectAttempt);
            instance.io.off('reconnect', onReconnect);
            instance.off('connect_error', onConnectError);
            instance.off('chat:conversation-updated', scheduleUnreadRefresh);
            instance.off('chat:message', scheduleUnreadRefresh);
            instance.off('support:conversation-updated', scheduleUnreadRefresh);
            instance.off('support:message', scheduleUnreadRefresh);
            instance.disconnect();
            socketRef.current = null;
            setSocket(null);
            setConnectionStatus('idle');
        };
    }, [isAuthenticated, refreshUnreadCounts, scheduleUnreadRefresh]);

    const value = {
        socket,
        connectionStatus,
        teamChatUnread,
        supportInboxUnread,
        setTeamChatUnread,
        setSupportInboxUnread,
        refreshUnreadCounts,
    };

    return (
        <RealtimeContext.Provider value={value}>
            {children}
        </RealtimeContext.Provider>
    );
};

RealtimeProvider.propTypes = {
    children: PropTypes.node,
};
